import React from "react";
import { Container, Card, Badge } from "react-bootstrap";

/**
 * Simple React + Bootstrap Privacy Policy component (full version)
 * - Drop-in page component
 * - Includes all major points from your earlier policy text
 */
export default function PrivacyPolicy() {
  return (
    <Container className="py-4 h-100">
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <h2 className="mb-0">EMAlpha – Privacy Policy</h2>
            <Badge bg="secondary">Last Updated: [01 Jan 2026]</Badge>
          </div>

          <p className="text-muted mb-4">
            This Privacy Policy explains how <strong>EMAlpha LLC</strong> (“EMAlpha,”
            “we,” “us,” or “our”) collects, uses, discloses, and protects information
            when you use our mobile applications, including but not limited to{" "}
            <strong>CoTrader</strong> and <strong>MacroMonitor</strong> (collectively,
            the “Apps”). By accessing or using the Apps, you agree to the practices
            described in this Privacy Policy.
          </p>

          <hr />

          <section className="mb-4">
            <h5 className="mb-2">1. Information We Collect</h5>

            <h6 className="mt-3">A. Information You Provide</h6>
            <ul className="mb-3">
              <li>Name, email address, and account credentials</li>
              <li>Organization or employer (if applicable)</li>
              <li>
                Subscription and billing details (processed via third-party payment
                providers)
              </li>
              <li>Communications, feedback, or support requests</li>
            </ul>

            <h6 className="mt-3">B. Automatically Collected Information</h6>
            <ul className="mb-3">
              <li>Device identifiers, operating system, and app version</li>
              <li>IP address and approximate location (country/region level)</li>
              <li>Usage data, interaction logs, feature access, and session metadata</li>
              <li>Crash reports, diagnostics, and performance metrics</li>
            </ul>

            <h6 className="mt-3">C. AI &amp; Analytics Inputs</h6>
            <ul className="mb-0">
              <li>
                If you enter queries, prompts, filters, or parameters, we process them
                to provide functionality and generate outputs.
              </li>
              <li>
                Inputs and generated outputs may be logged temporarily for security,
                quality, and performance monitoring.
              </li>
              <li>
                We do not require or intentionally collect sensitive personal data.
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">2. How We Use Information</h5>
            <ul className="mb-0">
              <li>Provide, operate, and maintain the Apps</li>
              <li>Authenticate users and manage accounts</li>
              <li>Deliver analytics, insights, and AI-generated outputs</li>
              <li>Improve performance, reliability, and security</li>
              <li>Process subscriptions and payments</li>
              <li>Comply with legal and regulatory obligations</li>
              <li>Detect fraud, abuse, or misuse</li>
            </ul>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">3. AI Models &amp; Data Usage</h5>
            <ul className="mb-0">
              <li>User inputs may be processed by proprietary or third-party AI models.</li>
              <li>
                Inputs and outputs are <strong>not used to train public or third-party models</strong>{" "}
                unless explicitly disclosed.
              </li>
              <li>EMAlpha does not claim ownership over your raw inputs.</li>
            </ul>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">4. Sharing &amp; Disclosure</h5>
            <p className="mb-2">
              We may share information only in the following circumstances:
            </p>
            <ul className="mb-2">
              <li>
                With trusted service providers (hosting, analytics, payments, security)
              </li>
              <li>To comply with law, regulation, or legal process</li>
              <li>To protect EMAlpha’s rights, users, or system integrity</li>
              <li>
                In connection with a corporate transaction (merger, acquisition,
                restructuring)
              </li>
            </ul>
            <p className="mb-0">
              <strong>We do not sell personal data.</strong>
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">5. Third-Party Services</h5>
            <p className="mb-0">
              The Apps may integrate with third-party platforms or data sources. Their
              data practices are governed by their own policies. EMAlpha is not
              responsible for third-party privacy practices.
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">6. Data Retention</h5>
            <p className="mb-0">
              We retain information only as long as necessary to provide the Apps,
              meet legal, regulatory, and audit requirements, and enforce agreements
              and protect systems. Data may be anonymized or aggregated where
              appropriate.
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">7. Security Measures</h5>
            <p className="mb-2">
              We implement reasonable administrative, technical, and organizational
              safeguards, including:
            </p>
            <ul className="mb-2">
              <li>Encryption in transit and at rest</li>
              <li>Access controls and monitoring</li>
              <li>Secure infrastructure and logging</li>
            </ul>
            <p className="mb-0">
              No system is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">8. Your Rights &amp; Choices</h5>
            <p className="mb-2">
              Depending on your jurisdiction, you may have the right to access or
              correct your information, request deletion of your account or data, or
              object to or restrict certain processing.
            </p>
            <p className="mb-0">
              Requests may be submitted to: <strong>privacy@emalpha.com</strong>
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">9. International Data Transfers</h5>
            <p className="mb-0">
              The Apps are operated globally. Your information may be processed in the
              United States or other jurisdictions with different data protection
              laws. By using the Apps, you consent to such transfers.
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">10. Children’s Privacy</h5>
            <p className="mb-0">
              The Apps are not intended for users under 18. We do not knowingly
              collect data from minors.
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">11. Changes to This Policy</h5>
            <p className="mb-0">
              We may update this Privacy Policy from time to time. Continued use of
              the Apps constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-2">
            <h5 className="mb-2">12. Contact Information</h5>
            <p className="mb-0">
              <strong>EMAlpha LLC</strong>
              <br />
              📧 <strong>support@emalpha.com</strong>
            </p>
          </section>
        </Card.Body>
      </Card>
    </Container>
  );
}
